// Tiny Web Audio cues — generated, no asset files needed.
// One shared AudioContext, lazy-initialized on first user gesture so we
// don't fall foul of browser autoplay policies. All sounds are short and
// gentle (kid-friendly volume).

export type Cue =
  | "click"
  | "blip"
  | "dingUp"
  | "dingDown"
  | "munch"
  | "correct"
  | "wrong";

let ctx: AudioContext | null = null;
let enabled = false;

function ensureCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!Ctor) return null;
  ctx = new Ctor();
  return ctx;
}

export function setSoundEnabled(on: boolean): void {
  enabled = on;
  if (on) {
    // resume on user gesture so subsequent plays work everywhere
    const c = ensureCtx();
    if (c?.state === "suspended") void c.resume();
  }
}

export function isSoundEnabled(): boolean {
  return enabled;
}

function tone(
  freq: number,
  durMs: number,
  type: OscillatorType = "sine",
  gain = 0.08,
  startOffsetMs = 0,
): void {
  const c = ctx;
  if (!c) return;
  const now = c.currentTime + startOffsetMs / 1000;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(gain, now + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, now + durMs / 1000);
  osc.connect(g).connect(c.destination);
  osc.start(now);
  osc.stop(now + durMs / 1000 + 0.02);
}

function slide(
  fromHz: number,
  toHz: number,
  durMs: number,
  type: OscillatorType = "sine",
  gain = 0.08,
): void {
  const c = ctx;
  if (!c) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(fromHz, now);
  osc.frequency.exponentialRampToValueAtTime(
    Math.max(20, toHz),
    now + durMs / 1000,
  );
  g.gain.setValueAtTime(0, now);
  g.gain.linearRampToValueAtTime(gain, now + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, now + durMs / 1000);
  osc.connect(g).connect(c.destination);
  osc.start(now);
  osc.stop(now + durMs / 1000 + 0.02);
}

export function play(cue: Cue): void {
  if (!enabled) return;
  const c = ensureCtx();
  if (!c) return;
  if (c.state === "suspended") void c.resume();

  switch (cue) {
    case "click":
      tone(520, 60, "triangle", 0.05);
      return;
    case "blip":
      tone(880, 90, "sine", 0.07);
      tone(1320, 60, "sine", 0.04, 40);
      return;
    case "dingUp":
      tone(660, 120, "triangle", 0.08);
      tone(990, 160, "sine", 0.07, 90);
      return;
    case "dingDown":
      tone(440, 140, "sine", 0.07);
      tone(330, 180, "sine", 0.06, 90);
      return;
    case "munch":
      slide(180, 90, 140, "square", 0.05);
      tone(660, 90, "triangle", 0.06, 120);
      return;
    case "correct":
      tone(523.25, 120, "triangle", 0.07); // C5
      tone(659.25, 140, "triangle", 0.07, 90); // E5
      tone(783.99, 200, "triangle", 0.08, 200); // G5
      return;
    case "wrong":
      slide(330, 165, 260, "sine", 0.07);
      return;
  }
}
